import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BugReportDialog } from './BugReportDialog';
import { useTranslation } from '../hooks/useTranslation';
import { Settings } from 'lucide-react';
import { captureScreenshotWithPreview } from '../utils/bugReportUtils';
import { useSensitiveDataBlur } from '../hooks/useSensitiveDataBlur';

interface BugReportContextMenuProps {
  children: React.ReactNode;
}

export const BugReportContextMenu: React.FC<BugReportContextMenuProps> = ({
  children,
}) => {
  const { translate } = useTranslation();
  const { activateBlur, deactivateBlur } = useSensitiveDataBlur();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showBugReportDialog, setShowBugReportDialog] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      // Check if right-click is on the document body or main content
      const target = e.target as HTMLElement;

      // Don't show context menu on certain elements
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.closest('button') ||
        target.closest('[role="button"]')
      ) {
        return;
      }

      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY });
    };

    const handleClick = (e: MouseEvent) => {
      // Don't close context menu if clicking inside it
      const target = e.target as HTMLElement;
      if (target.closest('[data-bug-report-context-menu]')) {
        // Allow the click to proceed normally for menu items
        return;
      }
      setContextMenu(null);
    };

    // Handle screenshot key (Print Screen, F12, or Ctrl+Shift+S)
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Print Screen key (F13 on some keyboards, or PrintScreen)
      // Also support Cmd+Shift+S on Mac or Ctrl+Shift+S on Windows/Linux
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isPrintScreen =
        e.key === 'PrintScreen' ||
        e.key === 'F13' ||
        (e.key === 'F12' &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.altKey &&
          !e.metaKey) ||
        (isMac && e.metaKey && e.shiftKey && e.key.toLowerCase() === 's') ||
        (!isMac && e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 's');

      if (isPrintScreen) {
        e.preventDefault();
        e.stopPropagation();
        (async () => {
          try {
            console.log('[Bug Report] Starting capture (keyboard shortcut)');
            // Activate blur before capture
            activateBlur();
            // Wait for blur to apply
            await new Promise((resolve) => setTimeout(resolve, 100));
            
            await captureScreenshotWithPreview();
            
            // Deactivate blur after capture
            deactivateBlur();
            
            console.log('[Bug Report] Capture completed, opening dialog');
            setShowBugReportDialog(true);
          } catch (error) {
            console.error('[Bug Report] Error capturing screenshot:', error);
            deactivateBlur(); // Ensure blur is deactivated on error
          }
        })();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    // Use bubble phase instead of capture to allow menu items to handle clicks first
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleReportBug = async () => {
    setContextMenu(null);
    try {
      console.log('[Bug Report] Starting capture before dialog opens');
      // Activate blur before capture
      activateBlur();
      // Wait for blur to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      await captureScreenshotWithPreview();
      
      // Deactivate blur after capture
      deactivateBlur();

      console.log('[Bug Report] Capture completed, opening dialog');
      setShowBugReportDialog(true);
    } catch (error) {
      console.error('[Bug Report] Error capturing screenshot:', error);
      deactivateBlur(); // Ensure blur is deactivated on error
    }
  };

  // Check if we're in development/local environment
  const isLocal =
    import.meta.env.DEV ||
    import.meta.env.MODE === 'development' ||
    window.location.hostname === 'localhost';

  const handleOpenDevTools = () => {
    setContextMenu(null);
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    // Log helpful information to console
    console.log(
      '%c🔧 Developer Tools Instructions',
      'color: #3b82f6; font-size: 16px; font-weight: bold;'
    );
    console.log(
      '%cTo open Developer Tools:',
      'color: #059669; font-size: 14px; font-weight: bold;'
    );
    if (isMac) {
      console.log(
        '%c• Press Cmd + Option + I',
        'color: #6b7280; font-size: 12px;'
      );
      console.log(
        '%c• Or press Cmd + Option + J for Console',
        'color: #6b7280; font-size: 12px;'
      );
      console.log(
        '%c• Or right-click and select "Inspect Element"',
        'color: #6b7280; font-size: 12px;'
      );
    } else {
      console.log('%c• Press F12', 'color: #6b7280; font-size: 12px;');
      console.log(
        '%c• Or press Ctrl + Shift + I',
        'color: #6b7280; font-size: 12px;'
      );
      console.log(
        '%c• Or press Ctrl + Shift + J for Console',
        'color: #6b7280; font-size: 12px;'
      );
      console.log(
        '%c• Or right-click and select "Inspect"',
        'color: #6b7280; font-size: 12px;'
      );
    }
    console.log('%c─────────────────────────────────────', 'color: #d1d5db;');

    // Show user-friendly alert with instructions
    const message = isMac
      ? '🔧 Developer Tools\n\nPress: Cmd + Option + I\nOr: Cmd + Option + J (Console)\n\nInstructions have been logged to the console.'
      : '🔧 Developer Tools\n\nPress: F12\nOr: Ctrl + Shift + I\nOr: Ctrl + Shift + J (Console)\n\nInstructions have been logged to the console.';

    alert(message);
  };

  // Get or create a dedicated portal container for context menu
  const getPortalContainer = () => {
    let container = document.getElementById('bug-report-context-menu-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'bug-report-context-menu-portal';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '10000';
      document.body.appendChild(container);
    }
    return container;
  };

  const contextMenuContent = contextMenu ? (
    <div
      data-bug-report-context-menu="true"
      className="fixed min-w-[200px] rounded-md border border-border bg-popover text-popover-foreground shadow-md py-1"
      style={{
        left: `${contextMenu.x}px`,
        top: `${contextMenu.y}px`,
        zIndex: 10000,
        position: 'fixed',
        pointerEvents: 'auto',
      }}
      onClick={(e) => {
        // Set global flag to prevent dialog from closing
        (window as any).__contextMenuClicked = true;
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        // Set global flag to prevent dialog from closing
        (window as any).__contextMenuClicked = true;
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-sm px-4 py-2 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground active:bg-accent/80"
        onClick={(e) => {
          // Set global flag to prevent dialog from closing
          (window as any).__contextMenuClicked = true;
          e.stopPropagation();
          e.preventDefault();
          handleReportBug();
        }}
        onMouseDown={(e) => {
          // Set global flag to prevent dialog from closing
          (window as any).__contextMenuClicked = true;
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        {translate('bugReport.title')}
      </button>
      {isLocal && (
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-sm border-t border-border px-4 py-2 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground active:bg-accent/80"
          onClick={(e) => {
            // Set global flag to prevent dialog from closing
            (window as any).__contextMenuClicked = true;
            e.stopPropagation();
            e.preventDefault();
            handleOpenDevTools();
          }}
          onMouseDown={(e) => {
            // Set global flag to prevent dialog from closing
            (window as any).__contextMenuClicked = true;
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Settings className="h-4 w-4" />
          Developer Tools
        </button>
      )}
    </div>
  ) : null;

  return (
    <>
      <div ref={containerRef}>{children}</div>
      {typeof window !== 'undefined' && contextMenuContent
        ? createPortal(contextMenuContent, getPortalContainer())
        : null}
      <BugReportDialog
        open={showBugReportDialog}
        onClose={() => setShowBugReportDialog(false)}
      />
    </>
  );
};
